/**
 * NDG Films Slider — view.js
 *
 * Initialise Embla Carousel (via le module partagé ndg/shared-slider).
 * Gère l'expansion hover + lecture vidéo (mp4 ou iframe YouTube) + toggle son.
 */

import { store, getElement } from '@wordpress/interactivity';
import { initSlider as initEmblaSlider } from '../../js/utils/slider.js';
import { hasAnalyticsConsent } from '../../js/front/consent-check.js';

store( 'ndg/films-slider', {
	callbacks: {
		initSlider() {
			const { ref } = getElement();
			if ( ! ref ) return;

			const embla = initEmblaSlider( ref, {
				wrapperSelector: '.ndg-films-slider',
				labelSelector:   '.ndg-films-slider__heading',
				fallbackLabel:   'Films',
				dragFree:        false,
				watchResize:     true,
			} );

			// ── Hover vidéo ────────────────────────────────────────────────
			// Uniquement sur devices souris (hover: hover)
			const canHover = window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;
			if ( ! canHover ) return;

			const slides = ref.querySelectorAll( '.ndg-film-card' );

			slides.forEach( ( slide ) => {
				const videoType = slide.dataset.videoType; // 'mp4' | 'youtube' | 'none'
				if ( videoType === 'none' ) return;

				const soundBtn   = slide.querySelector( '.ndg-film-card__sound' );
				const videoEl    = slide.querySelector( '.ndg-film-card__video' );
				const ytPlaceholder = slide.querySelector( '.ndg-film-card__video--yt' );
				let ytIframe     = null;
				let isMuted      = true;

				// ── Mouseenter : lecture ───────────────────────────────────
				slide.addEventListener( 'mouseenter', () => {
					// Ne pas lancer la vidéo pendant un drag Embla
					if ( ref.classList.contains( 'is-dragging' ) ) return;

					slide.classList.add( 'is-hovered' );

					if ( videoType === 'mp4' && videoEl ) {
						if ( ! videoEl.src && videoEl.dataset.src ) {
							videoEl.src = videoEl.dataset.src;
						}
						videoEl.muted = true;
						videoEl.play().catch( () => {} );

					} else if ( videoType === 'youtube' && ytPlaceholder ) {
						const ytId = slide.dataset.ytId;
						if ( ! ytId || ! hasAnalyticsConsent() ) return;

						if ( ! ytIframe ) {
							ytIframe = document.createElement( 'iframe' );
							ytIframe.src = [
								`https://www.youtube-nocookie.com/embed/${ ytId }`,
								`?autoplay=1&mute=1&loop=1&playlist=${ ytId }`,
								`&controls=0&rel=0&iv_load_policy=3&modestbranding=1`,
								`&playsinline=1&enablejsapi=1`,
							].join( '' );
							ytIframe.allow      = 'autoplay; encrypted-media';
							ytIframe.frameBorder = '0';
							ytPlaceholder.appendChild( ytIframe );
						} else {
							// Déjà créée : relance la lecture
							ytIframe.contentWindow?.postMessage(
								JSON.stringify( { event: 'command', func: 'playVideo' } ), '*'
							);
						}
					}
				} );

				// ── Mouseleave : pause + reset ─────────────────────────────
				slide.addEventListener( 'mouseleave', () => {
					slide.classList.remove( 'is-hovered' );

					if ( videoType === 'mp4' && videoEl ) {
						videoEl.pause();
						videoEl.currentTime = 0;
						videoEl.muted       = true;
					} else if ( ytIframe ) {
						ytIframe.contentWindow?.postMessage(
							JSON.stringify( { event: 'command', func: 'pauseVideo' } ), '*'
						);
						// Re-mute pour la prochaine fois
						ytIframe.contentWindow?.postMessage(
							JSON.stringify( { event: 'command', func: 'mute' } ), '*'
						);
					}

					// Reset son
					isMuted = true;
					if ( soundBtn ) soundBtn.classList.remove( 'is-unmuted' );
				} );

				// ── Bouton son ─────────────────────────────────────────────
				if ( soundBtn ) {
					soundBtn.addEventListener( 'click', ( e ) => {
						e.preventDefault();
						e.stopPropagation(); // évite de suivre le lien du slide

						isMuted = ! isMuted;
						soundBtn.classList.toggle( 'is-unmuted', ! isMuted );

						if ( videoType === 'mp4' && videoEl ) {
							videoEl.muted = isMuted;

						} else if ( ytIframe ) {
							ytIframe.contentWindow?.postMessage(
								JSON.stringify( {
									event: 'command',
									func:  isMuted ? 'mute' : 'unMute',
								} ), '*'
							);
						}
					} );
				}
			} );
		},
	},
} );
